import React from 'react';
import { CollapsibleRow } from './CollapsibleRow';
import { HeaderTooltip } from './HeaderTooltip';
import '../inventoryHistoryV2.css';

/**
 * A table component that renders rows that can be expanded to show more details
 *
 * @param {Array} rows - The data to render in the table
 * @param {Array} columns - The column configuration
 * @param {Boolean} isLoading - Whether the table is loading
 * @param {Array} expandedFilters - The filters for expanded rows
 * @param {Function} setNotification - Function to set notifications
 * @param {Function} filtersToApiParams - Function to convert filters to API parameters
 * @param {string} facilityId - The facility ID for the current view
 * @param {boolean} summarizePackout - Whether to summarize packout events
 * @param {function} fetchAdjustmentReasons - Function to fetch adjustment reasons for modal
 * @param {string} groupBy - The current grouping mode ('sku' or 'lot')
 */
const CollapsibleTable = ({
    rows = [],
    columns = [],
    isLoading = false,
    expandedFilters = [],
    setNotification,
    filtersToApiParams,
    facilityId,
    summarizePackout = false,
    fetchAdjustmentReasons,
    groupBy = 'sku',
}) => {
    const renderHeader = () => {
        const identityColumn = columns[0];
        const valueColumns = columns.slice(1);

        const renderColumnHeader = (column, className) => (
            <th key={column.id} className={className}>
                {column.toolTipLabel ? (
                    <HeaderTooltip 
                        label={column.label}
                        tooltip={column.toolTipLabel}
                        className="text-center w-full block"
                    />
                ) : (
                    <span className="text-center w-full block">{column.label}</span>
                )}
            </th>
        );

        return (
            <thead className="sticky top-0 z-10">
                <tr className="shadow font-semibold">
                    {/* Column for expand/collapse button */}
                    <th className="w-[5%] p-2 h-12"></th>

                    {/* Identity column header with tooltip */}
                    {renderColumnHeader(identityColumn, "w-[20%] p-2 h-12 align-bottom")}

                    {/* Value column headers with tooltips */}
                    {valueColumns.map((column) => (
                        renderColumnHeader(column, "p-2 h-12 align-bottom")
                    ))}
                    
                    {/* Export button column header */}
                    <th className="w-[5%] p-2 h-12"></th>
                </tr>
            </thead>
        );
    };

    const renderEmptyState = () => (
        <tbody>
            <tr>
                <td colSpan={columns.length + 2} className="text-center p-8 bg-white">
                    <div className="text-gray-500 text-lg">No data available</div>
                </td>
            </tr>
        </tbody>
    );

    const renderLoading = () => (
        <tbody>
            <tr>
                <td colSpan={columns.length + 2} className="text-center p-8">
                    <div className="flex justify-center">
                        <div className="w-12 h-12 spinner animate-spin"></div>
                    </div>
                </td>
            </tr>
        </tbody>
    );

    if (isLoading) {
        return (
            <table className="w-full border-collapse">
                {renderHeader()}
                {renderLoading()}
            </table>
        );
    }

    if (!rows.length) {
        return (
            <table className="w-full border-collapse">
                {renderHeader()}
                {renderEmptyState()}
            </table>
        );
    }

    return (
        <table className="w-full border-collapse">
            {renderHeader()}
            <tbody>
                {rows.map((row, index) => (
                    <CollapsibleRow
                        key={`${row.id || row.sku || index}`}
                        row={row}
                        columns={columns}
                        expandedFilters={expandedFilters}
                        setNotification={setNotification}
                        filtersToApiParams={filtersToApiParams}
                        facilityId={facilityId}
                        summarizePackout={summarizePackout}
                        fetchAdjustmentReasons={fetchAdjustmentReasons}
                        groupBy={groupBy}
                    />
                ))}
            </tbody>
        </table>
    );
};

export { CollapsibleTable };
